// ======================================================================
// FILE: src/modules/wms/checklist/pages/ChecklistListPage.js
// MODULE: Work Management System (WMS)
// FEATURE: Checklist Management
// PURPOSE:
//   Checklist Listing Screen
// ======================================================================

import React, {
  useEffect,
  useState,
} from "react";

import {
  Link,
  useNavigate,
} from "react-router-dom";

import wmsChecklistService
from "../../services/wmsChecklistService";

export default function ChecklistListPage() {

  const navigate = useNavigate();

  const [loading, setLoading] =
    useState(false);

  const [rows, setRows] =
    useState([]);

  const [pagination, setPagination] =
    useState({});

  const [summary, setSummary] =
    useState({
      totalChecklists: 0,
      activeChecklists: 0,
      scoringEnabled: 0,
      approvalRequired: 0,
    });

  const [filters, setFilters] =
    useState({
      q: "",
      applicable_work_item_type: "",
      page: 1,
      per_page: 20,
    });

  // ==========================================================
  // LOAD
  // ==========================================================

  async function loadData() {

    try {

      setLoading(true);

      const result =
        await wmsChecklistService
          .listChecklists(filters);

      const items =
        result.items || [];

      setRows(items);

      setPagination(
        result.pagination || {}
      );

      let activeCount = 0;
      let scoringCount = 0;
      let approvalCount = 0;

      items.forEach((row) => {

        if (row.is_active) {
          activeCount += 1;
        }

        if (
          row.is_scoring_enabled
        ) {
          scoringCount += 1;
        }

        if (
          row.approval_required
        ) {
          approvalCount += 1;
        }

      });

      setSummary({

        totalChecklists:
          result.pagination?.total
          || items.length,

        activeChecklists:
          activeCount,

        scoringEnabled:
          scoringCount,

        approvalRequired:
          approvalCount,
      });

    } catch (err) {

      console.error(err);

      alert(
        err?.response?.data?.message
        || "Failed to load checklists"
      );

    } finally {

      setLoading(false);
    }
  }

  useEffect(() => {

    loadData();

  }, [filters.page]);

  // ==========================================================
  // SEARCH
  // ==========================================================

  function handleSearch() {

    setFilters((prev) => ({
      ...prev,
      page: 1,
    }));

    loadData();
  }

  // ==========================================================
  // DELETE
  // ==========================================================

  async function handleDelete(row) {

    const ok = window.confirm(
      `Delete Checklist ${row.checklist_name}?`
    );

    if (!ok) {
      return;
    }

    try {

      await wmsChecklistService
        .deleteChecklist(
          row.id
        );

      await loadData();

    } catch (err) {

      alert(
        err?.response?.data?.message
        || "Delete failed"
      );
    }
  }

  // ==========================================================
  // PAGINATION
  // ==========================================================

  function changePage(page) {

    setFilters((prev) => ({
      ...prev,
      page,
    }));
  }

  // ==========================================================
  // UI
  // ==========================================================

  return (

    <div className="container-fluid">

      {/* ==================================================== */}
      {/* PAGE HEADER */}
      {/* ==================================================== */}

      <div
        className="card border-0 mb-4"
        style={{
          background:
            "linear-gradient(90deg,#0d6efd,#4f8cff)",
          borderRadius: "12px",
          color: "#fff",
        }}
      >

        <div
          className="
            card-body
            d-flex
            justify-content-between
            align-items-center
          "
        >

          <div>

            <h2 className="mb-1">
              Checklist Management
            </h2>

            <div>
              Manage reusable inspection
              and audit checklists
            </div>

          </div>

          <div>

            <button
              className="btn btn-light me-2"
              onClick={loadData}
            >
              <i className="fas fa-sync-alt me-2" />
              Refresh
            </button>

            <Link
              to="/wms/checklists/create"
              className="btn btn-outline-light"
            >
              <i className="fas fa-plus me-2" />
              Create Checklist
            </Link>

          </div>

        </div>

      </div>

      {/* ==================================================== */}
      {/* KPI */}
      {/* ==================================================== */}

      <div className="row mb-4">

        <div className="col-md-3">
          <div
            className="card shadow-sm h-100"
            style={{
              borderTop:
                "4px solid #0d6efd",
            }}
          >
            <div className="card-body d-flex justify-content-between">
              <div>
                <div className="text-muted">
                  Total Checklists
                </div>
                <h2>
                  {summary.totalChecklists}
                </h2>
              </div>
              <i
                className="fas fa-clipboard-check"
                style={{
                  fontSize: 28,
                  color: "#0d6efd",
                }}
              />
            </div>
          </div>
        </div>

        <div className="col-md-3">
          <div
            className="card shadow-sm h-100"
            style={{
              borderTop:
                "4px solid #28a745",
            }}
          >
            <div className="card-body d-flex justify-content-between">
              <div>
                <div className="text-muted">
                  Active Checklists
                </div>
                <h2>
                  {summary.activeChecklists}
                </h2>
              </div>
              <i
                className="fas fa-check-circle"
                style={{
                  fontSize: 28,
                  color: "#28a745",
                }}
              />
            </div>
          </div>
        </div>

        <div className="col-md-3">
          <div
            className="card shadow-sm h-100"
            style={{
              borderTop:
                "4px solid #fd7e14",
            }}
          >
            <div className="card-body d-flex justify-content-between">
              <div>
                <div className="text-muted">
                  Scoring Enabled
                </div>
                <h2>
                  {summary.scoringEnabled}
                </h2>
              </div>
              <i
                className="fas fa-star"
                style={{
                  fontSize: 28,
                  color: "#fd7e14",
                }}
              />
            </div>
          </div>
        </div>

        <div className="col-md-3">
          <div
            className="card shadow-sm h-100"
            style={{
              borderTop:
                "4px solid #6f42c1",
            }}
          >
            <div className="card-body d-flex justify-content-between">
              <div>
                <div className="text-muted">
                  Approval Required
                </div>
                <h2>
                  {summary.approvalRequired}
                </h2>
              </div>
              <i
                className="fas fa-user-check"
                style={{
                  fontSize: 28,
                  color: "#6f42c1",
                }}
              />
            </div>
          </div>
        </div>

      </div>

      {/* SEARCH */}

      <div className="card shadow-sm mb-4">

        <div className="card-body">

          <div className="row">

            <div className="col-md-4">

              <label>Search</label>

              <input
                className="form-control"
                placeholder="Code / Name"
                value={filters.q}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    q: e.target.value,
                  })
                }
              />

            </div>

            <div className="col-md-3">

              <label>
                Work Item Type
              </label>

              <select
                className="form-control"
                value={
                  filters.applicable_work_item_type
                }
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    applicable_work_item_type:
                      e.target.value,
                  })
                }
              >

                <option value="">
                  All
                </option>

                <option value="TASK">
                  Task
                </option>

                <option value="AUDIT">
                  Audit
                </option>

                <option value="OBSERVATION">
                  Observation
                </option>

                <option value="CAPA">
                  CAPA
                </option>

              </select>

            </div>

            <div
              className="
                col-md-2
                d-flex
                align-items-end
              "
            >
              <button
                className="btn btn-primary"
                onClick={handleSearch}
              >
                Search
              </button>
            </div>

          </div>

        </div>

      </div>

      {/* GRID */}

      <div className="card shadow-sm">

        <div className="card-body p-0">

          <table
            className="
              table
              table-hover
              table-striped
              align-middle
              mb-0
            "
          >

            <thead className="table-light">

              <tr>

                <th>Code</th>
                <th>Name</th>
                <th>Work Item Type</th>
                <th>Category</th>
                <th>Scoring</th>
                <th>Approval</th>
                <th>Status</th>
                <th width="220">
                  Actions
                </th>

              </tr>

            </thead>

            <tbody>

              {loading && (
                <tr>
                  <td
                    colSpan="8"
                    className="text-center"
                  >
                    Loading...
                  </td>
                </tr>
              )}

              {!loading &&
                rows.length === 0 && (
                <tr>
                  <td
                    colSpan="8"
                    className="text-center"
                  >
                    No Records Found
                  </td>
                </tr>
              )}

              {!loading &&
                rows.map((row) => (

                <tr key={row.id}>

                  <td>
                    {row.checklist_code}
                  </td>

                  <td>
                    {row.checklist_name}
                  </td>

                  <td>
                    <span className="badge bg-info">
                      {
                        row.applicable_work_item_type
                      }
                    </span>
                  </td>

                  <td>
                    {row.category}
                  </td>

                  <td>

                    <span
                      className={
                        row.is_scoring_enabled
                          ? "badge bg-success"
                          : "badge bg-secondary"
                      }
                    >
                      {
                        row.is_scoring_enabled
                          ? "Enabled"
                          : "Disabled"
                      }
                    </span>

                  </td>

                  <td>

                    <span
                      className={
                        row.approval_required
                          ? "badge bg-primary"
                          : "badge bg-secondary"
                      }
                    >
                      {
                        row.approval_required
                          ? "Yes"
                          : "No"
                      }
                    </span>

                  </td>

                  <td>

                    <span
                      className={
                        row.is_active
                          ? "badge bg-success"
                          : "badge bg-danger"
                      }
                    >
                      {
                        row.is_active
                          ? "Active"
                          : "Inactive"
                      }
                    </span>

                  </td>

                  <td>

                    <button
                      className="
                        btn
                        btn-sm
                        btn-primary
                        me-1
                      "
                      onClick={() =>
                        navigate(
                          `/wms/checklists/${row.id}/edit`
                        )
                      }
                    >
                      Open
                    </button>

                    <button
                      className="
                        btn
                        btn-sm
                        btn-danger
                      "
                      onClick={() =>
                        handleDelete(row)
                      }
                    >
                      Delete
                    </button>

                  </td>

                </tr>

              ))}

            </tbody>

          </table>

        </div>

        <div className="card-footer">

          <div
            className="
              d-flex
              justify-content-between
              align-items-center
            "
          >

            <div>
              Total:
              {" "}
              {pagination.total || 0}
            </div>

            <div>

              <button
                className="
                  btn
                  btn-sm
                  btn-outline-secondary
                  me-2
                "
                disabled={
                  (pagination.page || 1)
                  <= 1
                }
                onClick={() =>
                  changePage(
                    (pagination.page || 1) - 1
                  )
                }
              >
                Previous
              </button>

              <span>

                Page
                {" "}
                {pagination.page || 1}
                {" "}
                of
                {" "}
                {pagination.pages || 1}

              </span>

              <button
                className="
                  btn
                  btn-sm
                  btn-outline-secondary
                  ms-2
                "
                disabled={
                  (pagination.page || 1)
                  >=
                  (pagination.pages || 1)
                }
                onClick={() =>
                  changePage(
                    (pagination.page || 1) + 1
                  )
                }
              >
                Next
              </button>

            </div>

          </div>

        </div>

      </div>

    </div>
  );
}